<template>
  <canvas></canvas>
</template>

<script>
import canvasUtils from './modules/canvasUtils.js';
import WaveformDigest from '../../../modules/WaveformDigest.js';
import timelineCanvas from './mixins/timelineCanvas.js';
import LoopDefinition from '../../../modules/LoopDefinition.js';
import Color from '../../../modules/Color.js';
import { TimeValue, TimeUnit } from '../../../modules/TimeValue.js';

const timelineGridIntervalPxMin = 120;

const timelineGridIntervalByLevel = [
  TimeValue.msec(10), // 10 milliseconds
  TimeValue.msec(25), // 25 milliseconds
  TimeValue.msec(50), // 50 milliseconds
  TimeValue.msec(100), // 100 milliseconds
  TimeValue.msec(250), // 250 milliseconds
  TimeValue.msec(500), // 500 milliseconds
  TimeValue.sec(1), // 1 second
  TimeValue.sec(2.5), // 2.5 seconds
  TimeValue.sec(5), // 5 seconds
  TimeValue.sec(10), // 10 seconds
  TimeValue.sec(30), // 30 seconds
  TimeValue.min(1), // 1 minute
  TimeValue.min(5), // 5 minute
  TimeValue.min(10), // 10 minutes
  TimeValue.min(30), // 30 minutes
  TimeValue.hour(1), // 1 hour
];

const loopIndicatorHeightPx = 3;

class TimelineGridBarTimeAndOffsetPx {
  static name = 'TimelineGridBarTimeAndOffsetPx';

  constructor(time, offsetPx) {
    this.time = time;
    this.offsetPx = offsetPx;
  }
}

const waveformColor = new Color(220, 220, 220);
const borderAndTextColor = new Color(210, 210, 210);
const timelineTrackOverlayColor = new Color(0, 0, 0, 0.05);
const rulerBackgroundColor = new Color(255, 255, 255);
const loopAreaColor = new Color(34, 200, 200);

export default {
  mixins: [
    timelineCanvas,
  ],

  watch: {
    numVisibleTimelines() { this.$_setDirty(true) },
    verticalOffsetPx() { this.$_setDirty(true) },
    timelineHeightPx() { this.$_setDirty(true) },
    timelineViewBegin: {
      handler() { this.$_setDirty(true) },
      deep: true
    },
    timelineViewEnd: {
      handler() { this.$_setDirty(true) },
      deep: true
    },
    isLoopEnabled() { this.$_setDirty(true) },
    waveformDigest() { this.$_setDirty(true) },
    loopDefinition: {
      handler() { this.$_setDirty(true) },
      deep: true,
    },
    waveformScale() { this.$_setDirty(true); },
  },

  props: {
    rulerVerticalOffsetPx: { type: Number },
    numVisibleTimelines:   { type: Number },
    verticalOffsetPx:      { type: Number },
    timelineHeightPx:      { type: Number },
    waveformDigest:        { type: WaveformDigest },
    timelineViewBegin:     { type: TimeValue },
    timelineViewEnd:       { type: TimeValue },
    timelineViewDuration:  { type: TimeValue },
    isLoopEnabled:         { type: Boolean },
    loopDefinition:        { type: LoopDefinition },
    waveformScale:         { type: Number },
  },

  computed: {
    $_currentWaveformDigest() {
      if (this.waveformDigest === undefined) return undefined;
      let samplingRateDotByDot = this.$data.$_timelineCanvasWidthPx / this.timelineViewDuration.sec;
      return this.waveformDigest.getDigest(samplingRateDotByDot);
    },

    $_timelineGridBarTimeAndOffsetPxArray() {
      {
        let timelineGridInterval = getTimelineGridInterval.call(this);
        let firstGridOffset = minimumMultipleGreaterThan(timelineGridInterval, this.timelineViewBegin);
        let firstGridTimelineViewOffset = firstGridOffset.getOffsetFrom(this.timelineViewBegin);
        let timelineGridBarTimeMsecAndOffsetPxArray = [];
        for (
          let currentGridTimelineViewOffset = firstGridTimelineViewOffset;
          currentGridTimelineViewOffset.isLessThan(this.timelineViewDuration);
          currentGridTimelineViewOffset.addSelf(timelineGridInterval)
        ) {
          let currentGridTime = this.timelineViewBegin.add(currentGridTimelineViewOffset).round(TimeUnit.units.msec);
          let currentGridTimelineViewOffsetpx = this.scaledTimeToPx(currentGridTimelineViewOffset);
          timelineGridBarTimeMsecAndOffsetPxArray.push(new TimelineGridBarTimeAndOffsetPx(currentGridTime, currentGridTimelineViewOffsetpx));
        }
        return timelineGridBarTimeMsecAndOffsetPxArray;
      }

      function getTimelineGridInterval() {
        let intervalMsec;
        for (let currentInterval of timelineGridIntervalByLevel) {
          intervalMsec = currentInterval;
          let currentIntervalPx = this.scaledTimeToPx(currentInterval);
          if (currentIntervalPx > timelineGridIntervalPxMin) {
            break;
          }
        }
        return intervalMsec;
      }

      function minimumMultipleGreaterThan(multiplier, value) {
        return value.divide(multiplier).ceil(TimeUnit.units.msec).multiply(multiplier);
      }
    },


    $_loopIndicatorDomRect() {
      if (this.timelineViewBegin.isGreaterThan(this.loopDefinition.end)) return undefined;
      if (this.timelineViewEnd.isLessThan(this.loopDefinition.begin)) return undefined;
      let loopBeginTime = (this.timelineViewBegin.isGreaterThan(this.loopDefinition.begin))? this.timelineViewBegin : this.loopDefinition.begin;
      let loopEndTime = (this.timelineViewEnd.isLessThan(this.loopDefinition.end))? this.timelineViewEnd : this.loopDefinition.end;
      let loopBeginTimelineViewOffset = loopBeginTime.getOffsetFrom(this.timelineViewBegin);
      let loopBeginTimelineViewOffsetPx = this.scaledTimeToPx(loopBeginTimelineViewOffset);
      let loopDuration = loopEndTime.getOffsetFrom(loopBeginTime);
      let loopDurationPx = this.scaledTimeToPx(loopDuration);
      return new DOMRect(
        loopBeginTimelineViewOffsetPx,
        this.rulerVerticalOffsetPx - loopIndicatorHeightPx,
        loopDurationPx,
        loopIndicatorHeightPx,
      );
    },
  },

  inject: [
    'pxToScaledTime',
    'scaledTimeToPx',
  ],

  methods: {
    /* public */
    draw() {
      this.$_draw(() => {
        if (this.waveformDigest) {
          drawWaveform.call(this);
        }
        drawTimelineTrack.call(this)
        drawRulerBackground.call(this);
        drawTimelineGridBars.call(this);
        drawTimelineGridBarTime.call(this);
        if (this.isLoopEnabled) {
          drawLoopArea.call(this);
        }
      });

      function drawBar(timeOffsetPx, color) {
        let timelineBackgroundCanvas = this.$data.$_timelineCanvasContext;
        canvasUtils.drawLine(
          timelineBackgroundCanvas,
          { x: timeOffsetPx, y: 0 },
          { y: this.$data.$_timelineCanvasHeightPx },
          { lineWidth: 1, strokeStyle: color, lineDash: [] },
        );
      }

      function drawWaveform() {
        {
          let numChannels = this.waveformDigest.numChannels;
          let waveformHeightAll = this.$data.$_timelineCanvasHeightPx - this.rulerVerticalOffsetPx;
          let eachWaveformHeight = waveformHeightAll / numChannels;
          let timelineBackgroundCanvas = this.$data.$_timelineCanvasContext;
          for (let channelIdx = 0; channelIdx < numChannels; ++channelIdx) {
            let waveformVerticalOffsetPx = getWaveformVerticalOffsetPx.call(this, eachWaveformHeight, channelIdx);
            canvasUtils.drawLine(
              timelineBackgroundCanvas,
              { x: 0, y: waveformVerticalOffsetPx },
              { x: this.$data.$_timelineCanvasWidthPx },
              { lineWidth: 1, strokeStyle: waveformColor.styleString, lineDash: [] },
            );
          }
          let eachWaveformAmplitude = eachWaveformHeight / 2 * this.waveformScale;
          let samplingRate = (this.$_currentWaveformDigest !== undefined)? this.$_currentWaveformDigest[0].samplingRate : this.waveformDigest.originalSamplingRate;
          let timelineViewBeginSampleOffset = this.timelineViewBegin.sec * samplingRate;
          let sampleInViewportInitialOffset = Math.floor(timelineViewBeginSampleOffset);
          if (sampleInViewportInitialOffset < 0) sampleInViewportInitialOffset = 0;
          let sampleInViewportFinalOffset = Math.ceil(this.timelineViewEnd.sec * samplingRate);
          let sampleFinalOffsetMax = (this.$_currentWaveformDigest !== undefined)? (this.$_currentWaveformDigest[0].max.length - 1) : (this.waveformDigest.originalWaveform[0].length - 1);
          if (sampleInViewportFinalOffset > sampleFinalOffsetMax) sampleInViewportFinalOffset = sampleFinalOffsetMax;
          let initialSampleTimelineViewOffsetPx = getTimelineViewOffsetPx.call(this, sampleInViewportInitialOffset, timelineViewBeginSampleOffset, samplingRate);
          for (let channelIdx = 0; channelIdx < numChannels; ++channelIdx) {
            let waveformVerticalOffsetPx = getWaveformVerticalOffsetPx.call(this, eachWaveformHeight, channelIdx);
            timelineBackgroundCanvas.beginPath();
            timelineBackgroundCanvas.lineWidth = 0.5;
            timelineBackgroundCanvas.moveTo(...canvasUtils.dotByDotOffsetCoordArgs(initialSampleTimelineViewOffsetPx, waveformVerticalOffsetPx));
            if (this.$_currentWaveformDigest !== undefined) {
              let currentLpcmDigestByChannel = this.$_currentWaveformDigest[channelIdx];
              timelineBackgroundCanvas.fillStyle = waveformColor.styleString;
              for (let currentSampleOffset = sampleInViewportInitialOffset; currentSampleOffset <= sampleInViewportFinalOffset; ++currentSampleOffset) {
                let currentSampleTimelineViewOffsetPx = getTimelineViewOffsetPx.call(this, currentSampleOffset, timelineViewBeginSampleOffset, samplingRate);
                let currentSampleVerticalOffsetPx = waveformVerticalOffsetPx - currentLpcmDigestByChannel.max[currentSampleOffset] * eachWaveformAmplitude;
                timelineBackgroundCanvas.lineTo(...canvasUtils.dotByDotOffsetCoordArgs(currentSampleTimelineViewOffsetPx, currentSampleVerticalOffsetPx));
              }
              for (let currentSampleOffset = sampleInViewportFinalOffset; currentSampleOffset >= sampleInViewportInitialOffset; --currentSampleOffset) {
                let currentSampleTimelineViewOffsetPx = getTimelineViewOffsetPx.call(this, currentSampleOffset, timelineViewBeginSampleOffset, samplingRate);
                let currentSampleVerticalOffsetPx = waveformVerticalOffsetPx - currentLpcmDigestByChannel.min[currentSampleOffset] * eachWaveformAmplitude;
                timelineBackgroundCanvas.lineTo(...canvasUtils.dotByDotOffsetCoordArgs(currentSampleTimelineViewOffsetPx, currentSampleVerticalOffsetPx));
              }
              timelineBackgroundCanvas.fill();
              timelineBackgroundCanvas.stroke();
            } else {
              let lpcmData = this.waveformDigest.originalWaveform;
              timelineBackgroundCanvas.strokeStyle = waveformColor.styleString;
              timelineBackgroundCanvas.setLineDash([]);
              timelineBackgroundCanvas.lineWidth = 1;
              for (let currentSampleOffset = sampleInViewportInitialOffset; currentSampleOffset <= sampleInViewportFinalOffset; ++currentSampleOffset) {
                let currentSampleTimelineViewOffsetPx = getTimelineViewOffsetPx.call(this, currentSampleOffset, timelineViewBeginSampleOffset, samplingRate);
                let currentSampleVerticalOffsetPx = waveformVerticalOffsetPx - lpcmData[channelIdx][currentSampleOffset] * eachWaveformAmplitude;
                timelineBackgroundCanvas.lineTo(...canvasUtils.dotByDotOffsetCoordArgs(currentSampleTimelineViewOffsetPx, currentSampleVerticalOffsetPx));
              }
              timelineBackgroundCanvas.stroke();
            }
          }
        }

        function getWaveformVerticalOffsetPx(eachWaveformHeight, channelIdx) {
          return this.rulerVerticalOffsetPx + (eachWaveformHeight * (channelIdx + 0.5));
        }

        function getTimelineViewOffsetPx(sampleOffset, timelineViewBeginSampleOffset, samplingRate) {
          let currentLocalSampleOffset = sampleOffset - timelineViewBeginSampleOffset;
          let currentSampleTimelineViewOffsetSec = currentLocalSampleOffset / samplingRate;
          return Math.round(this.scaledTimeToPx(TimeValue.sec(currentSampleTimelineViewOffsetSec)));
        }
      }

      function drawTimelineGridBars() {
        for (let timelineGridBarTimeAndOffsetPx of this.$_timelineGridBarTimeAndOffsetPxArray) {
          drawBar.call(this, timelineGridBarTimeAndOffsetPx.offsetPx, borderAndTextColor.styleString);
        }
      }

      function drawTimelineTrack() {
        let timelineBackgroundCanvas = this.$data.$_timelineCanvasContext;
        for (let timelineDataIdx = 0; timelineDataIdx < this.numVisibleTimelines; ++timelineDataIdx) {
          let trackBeginVerticalOffsetPx = this.verticalOffsetPx + this.timelineHeightPx * timelineDataIdx;
          let trackEndVerticalOffsetPx = this.verticalOffsetPx + this.timelineHeightPx * (timelineDataIdx + 1);
          if (timelineDataIdx % 2 === 1) {
            canvasUtils.drawRect(
              timelineBackgroundCanvas,
              {
                y: trackBeginVerticalOffsetPx,
                width: this.$data.$_timelineCanvasWidthPx,
                height: this.timelineHeightPx,
              },
              {
                fill: true,
                fillStyle: timelineTrackOverlayColor.styleString,
              },
            );
          }
          canvasUtils.drawLine(
            timelineBackgroundCanvas,
            { x: 0, y: trackEndVerticalOffsetPx },
            { x: this.$data.$_timelineCanvasWidthPx },
            { lineWidth: 1, strokeStyle: borderAndTextColor.styleString, lineDash: [] },
          );
        }
      }

      function drawRulerBackground() {
        let timelineBackgroundCanvas = this.$data.$_timelineCanvasContext;
        canvasUtils.drawRect(
          timelineBackgroundCanvas,
          {
            width: this.$data.$_timelineCanvasWidthPx,
            height: this.rulerVerticalOffsetPx,
          },
          {
            fill: true,
            fillStyle: rulerBackgroundColor.styleString,
          },
        );
      }

      function drawTimelineGridBarTime() {
        let timelineBackgroundCanvas = this.$data.$_timelineCanvasContext;
        for (let timelineGridBarTimeAndOffsetPx of this.$_timelineGridBarTimeAndOffsetPxArray) {
          let time = timelineGridBarTimeAndOffsetPx.time;
          let offsetPx = timelineGridBarTimeAndOffsetPx.offsetPx;
          timelineBackgroundCanvas.font = 'normal 9px sans-serif';
          timelineBackgroundCanvas.fillStyle = borderAndTextColor.styleString;
          timelineBackgroundCanvas.textAlign = 'left';
          timelineBackgroundCanvas.textBaseline = 'top';
          timelineBackgroundCanvas.fillText(time.toFormatString(), offsetPx + 2, 2);
        }
      }

      function drawLoopArea() {
        if (this.$_loopIndicatorDomRect === undefined) return;
        let timelineBackgroundCanvas = this.$data.$_timelineCanvasContext;
        canvasUtils.drawRect(
          timelineBackgroundCanvas,
          this.$_loopIndicatorDomRect,
          {
            fill: true,
            fillStyle: loopAreaColor.styleString,
          },
        );
      }
    },
  }
};
</script>
